/**
 * INVOICE QUERY SERVICE - PHASE 2
 * Applies filtering and sorting to BuildRight Construction invoice data
 */

import { QueryInterpretation } from './queryInterpreterWithContext';

export interface Invoice {
  invoiceId: string;
  invoiceNumber: string;
  customerName: string;
  amount: number;
  daysOverdue: number;
  dueDate: string;
  status?: string;
}

// BuildRight Construction - REAL overdue invoices data
// From accountingSystemData (Sales Ledger - customers owing us money)
// Data as of February 3, 2026
const BUILDRIGHT_INVOICES: Invoice[] = [
  {
    invoiceId: "INV-1234",
    invoiceNumber: "INV-1234",
    customerName: "BigCo Ltd",
    amount: 22000,
    daysOverdue: 5,
    dueDate: "2026-01-29",
    status: "overdue",
  },
  {
    invoiceId: "INV-1237",
    invoiceNumber: "INV-1237",
    customerName: "Major Construction Client Ltd",
    amount: 8700,
    daysOverdue: 11,
    dueDate: "2026-01-23",
    status: "overdue",
  },
  {
    invoiceId: "INV-1235",
    invoiceNumber: "INV-1235",
    customerName: "Local Government Authority",
    amount: 6900,
    daysOverdue: 16,
    dueDate: "2026-01-18",
    status: "overdue",
  },
  {
    invoiceId: "INV-1238",
    invoiceNumber: "INV-1238",
    customerName: "Education Facilities Ltd",
    amount: 5400,
    daysOverdue: 24,
    dueDate: "2026-01-10",
    status: "overdue",
  },
  {
    invoiceId: "INV-1236",
    invoiceNumber: "INV-1236",
    customerName: "Education Facilities Ltd",
    amount: 5147,
    daysOverdue: 20,
    dueDate: "2026-01-14",
    status: "overdue",
  },
];

/**
 * Apply filters and sorting to invoice data based on query interpretation
 */
export function queryInvoices(interpretation: QueryInterpretation): Invoice[] {
  let results = [...BUILDRIGHT_INVOICES];

  // Apply filters
  const { filters } = interpretation;

  // Filter by status
  if (filters.status) {
    results = results.filter(inv => inv.status === filters.status);
  }

  // Filter by customer name (case-insensitive partial match)
  if (filters.customerName) {
    const searchName = filters.customerName.toLowerCase();
    results = results.filter(inv => 
      inv.customerName.toLowerCase().includes(searchName)
    );
  }

  // Filter by minimum amount
  if (filters.minAmount !== undefined) {
    results = results.filter(inv => inv.amount >= filters.minAmount!);
  }

  // Filter by maximum amount
  if (filters.maxAmount !== undefined) {
    results = results.filter(inv => inv.amount <= filters.maxAmount!);
  }

  // Filter by minimum days overdue
  if (filters.minDaysOverdue !== undefined) {
    results = results.filter(inv => inv.daysOverdue >= filters.minDaysOverdue!);
  }

  // Filter by maximum days overdue
  if (filters.maxDaysOverdue !== undefined) {
    results = results.filter(inv => inv.daysOverdue <= filters.maxDaysOverdue!);
  }

  // Apply sorting
  const sortBy = interpretation.sortBy || 'amount';
  const sortOrder = interpretation.sortOrder || 'desc';

  results.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'amount':
        comparison = a.amount - b.amount;
        break;
      case 'daysOverdue':
        comparison = a.daysOverdue - b.daysOverdue;
        break;
      case 'dueDate':
        comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        break;
      case 'customerName':
        comparison = a.customerName.localeCompare(b.customerName);
        break;
      default:
        comparison = a.amount - b.amount;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  console.log('[InvoiceQueryService] Filtered results:', results.length, 'invoices');
  return results;
}

/**
 * Get all invoices (for testing/debugging)
 */
export function getAllInvoices(): Invoice[] {
  return [...BUILDRIGHT_INVOICES];
}
